
import Vue from "vue";

import { Component, Prop } from "vue-property-decorator";
import * as ST from "@/components/controls/s-table/exports";
import STable from "@/components/controls/s-table/sTable.vue";
import { storeApi } from "@/services/ApiService";
import {
  Agecheckitem,
  AgecheckitemAgeGroupEnum,
  Category
} from "@/api/api";
import TextEdit from "@/components/controls/text-edit.vue";

const MaxItemTextLength = 300;

interface AgeGroupTable {
  ageGroup: AgecheckitemAgeGroupEnum;
  title: string;
  dataSource: Agecheckitem[];
  settings: ST.Settings<Agecheckitem>;
}

@Component({ components: { STable } })
export default class AgecheckContent extends Vue {
  @Prop()
  category!: Category;

  notAvailable = false;

  groups: AgeGroupTable[] = [
    this.createGroup(AgecheckitemAgeGroupEnum.NUMBER_0, "15 år"),
    this.createGroup(AgecheckitemAgeGroupEnum.NUMBER_1, "18 år")
  ];

  async mounted() {
    await this.load();
  }

  public async load() {
    this.groups.forEach(g => (g.settings.loading = true));
    let items: Agecheckitem[] | undefined;
    try {
      items = (await storeApi.getAgeCheckItems(this.category.id))?.data;
    } catch {
      items = undefined;
    }
    // The api rejects categories that are not saved as Ålderskontroll yet
    this.notAvailable = !items;
    this.groups.forEach(g => {
      g.dataSource.splice(
        0,
        g.dataSource.length,
        ...(items ?? []).filter(i => i.ageGroup == g.ageGroup)
      );
      g.settings.loading = false;
    });
  }

  createGroup(
    ageGroup: AgecheckitemAgeGroupEnum,
    title: string
  ): AgeGroupTable {
    let dataSource: Agecheckitem[] = [];
    let settings: ST.Settings<Agecheckitem> = {
      dataSource: dataSource,
      pagination: false,
      showNewRow: true,
      newRowText: "Lägg till en ny rad",
      recordsUpdated: records => this.saveItems(records),
      newRowInit: () => ({ id: 0, ageGroup: ageGroup, itemText: "" }),
      loading: true,
      actions: {
        remove: {
          icon: "delete",
          confirmation: record =>
            `Är du säker på att du vill ta bort ${record.itemText}`,
          action: async record => {
            var recordIndex = dataSource.indexOf(record, 0);
            if (recordIndex > -1) {
              dataSource.splice(recordIndex, 1);
            }
            await storeApi.deleteAgeCheckItem(record.id, this.category.id);

            this.notify("Post borttagen", record.itemText);
          }
        }
      },
      columns: [
        {
          title: "Text",
          edit: TextEdit,
          editData: record => ({
            editable: true,
            dataBinding: {
              propertyName: "itemText"
            },

            updateEmitter: "valueChanged",
            props: {
              defaultValue: record.itemText
            }
          })
        }
      ]
    };
    return { ageGroup, title, dataSource, settings };
  }

  public async saveItems(records: Agecheckitem[]) {
    let invalid = records.find(
      r =>
        !r.itemText?.trim() || r.itemText.trim().length > MaxItemTextLength
    );
    if (invalid) {
      this.notify(
        "Kunde inte spara",
        `Texten måste vara 1-${MaxItemTextLength} tecken`
      );
      await this.load();
      return;
    }

    let result;
    try {
      result = await storeApi.updateAgeCheckItems(
        this.category.id,
        records.map(r => ({
          id: r.id,
          ageGroup: r.ageGroup,
          itemText: r.itemText.trim()
        }))
      );
    } catch {
      result = undefined;
    }
    if (!result?.data) {
      this.notify("Kunde inte spara", records[0].itemText);
      await this.load();
      return;
    }
    if (records.length == 1 && records[0].id == 0) {
      records[0].id = result.data[0].id;
    }
    this.notify("Ålderskontroll sparad", records[0].itemText);
  }

  public notify(title: string, message: string) {
    this.$notification.open({
      message: message,
      description: title,
      placement: "bottomRight",
      duration: 4
    });
  }
}
